import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { message } from 'antd';
import { jwtDecode } from 'jwt-decode';
import { setUser, clearUser } from '../store/interfaceSlice';

function readIsActiveFromToken(token) {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    if (decoded.exp * 1000 <= Date.now()) return false;
    return decoded.is_active !== false;
  } catch {
    return false;
  }
}

function readUserFromToken(token) {
  try {
    const decoded = jwtDecode(token);
    return {
      id: decoded.id,
      username: decoded.username,
      role: decoded.role || 'user',
    };
  } catch {
    return null;
  }
}

/**
 * Сессия и токен: восстановление из localStorage, login/logout.
 * @param {string|null} token — текущий токен из родителя (синхронизация с SocketProvider)
 * @param {function} setToken — setState токена в App
 */
export function useAuthSession(token, setToken) {
  const dispatch = useDispatch();
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('token'));
  const [isActive, setIsActive] = useState(() => readIsActiveFromToken(localStorage.getItem('token')));

  useEffect(() => {
    const hasToken = !!token && !!localStorage.getItem('token');
    setIsAuthenticated(hasToken);
    setIsActive(hasToken ? readIsActiveFromToken(token) : false);
  }, [token]);

  const restoreSession = useCallback(() => {
    const tokenFromStorage = localStorage.getItem('token');
    if (tokenFromStorage) {
      try {
        const decoded = jwtDecode(tokenFromStorage);
        if (decoded.exp * 1000 > Date.now()) {
          setIsAuthenticated(true);
          setIsActive(decoded.is_active !== false);
          const userData = readUserFromToken(tokenFromStorage);
          if (userData) {
            dispatch(setUser(userData));
          }
          if (decoded.is_active !== false) {
            message.success(`Добро пожаловать обратно, ${decoded.username}!`);
          }
        } else {
          localStorage.removeItem('token');
          setToken(null);
          setIsActive(false);
          dispatch(clearUser());
          message.error('Сессия истекла. Пожалуйста, войдите снова.');
        }
      } catch {
        localStorage.removeItem('token');
        setToken(null);
        setIsActive(false);
        dispatch(clearUser());
        message.error('Некорректный токен. Пожалуйста, войдите снова.');
      }
    }
  }, [dispatch, setToken]);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  const login = useCallback((newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setIsAuthenticated(true);
    setIsActive(readIsActiveFromToken(newToken));
    const userData = readUserFromToken(newToken);
    if (userData) {
      dispatch(setUser(userData));
    }
  }, [dispatch, setToken]);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setIsActive(false);
    localStorage.removeItem('token');
    setToken(null);
    dispatch(clearUser());
    message.success('Вы успешно вышли из системы.');
  }, [dispatch, setToken]);

  const refreshActivation = useCallback((newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setIsActive(true);
    const userData = readUserFromToken(newToken);
    if (userData) {
      dispatch(setUser(userData));
    }
    message.success('Учётная запись активирована!');
  }, [dispatch, setToken]);

  return {
    isAuthenticated,
    isActive,
    login,
    logout,
    restoreSession,
    refreshActivation,
  };
}
